import React, { useCallback } from 'react';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { PlanOperation } from '../../../types/plan';
import type { PlanningActionType, PlanningSelectionInfo } from '../../../types/planManagement';
import { buildShadeStructureOperation, isShadeStructureAction } from './planningStructuralObjects';

interface UsePlanningActionHandlerParams {
  planningSelection: PlanningSelectionInfo | null;
  onBedSelection?: (cells: Array<{ row: number; col: number }>) => void;
  onPatch: (ops: PlanOperation[]) => Promise<void>;
  clearPlanningSelection: () => void;
}

export const usePlanningActionHandler = ({
  planningSelection,
  onBedSelection,
  onPatch,
  clearPlanningSelection,
}: UsePlanningActionHandlerParams) => {
  return useCallback((action: PlanningActionType) => {
    if (!planningSelection || planningSelection.totalCells === 0) return;

    const cellsFromKeys = () => Array.from(planningSelection.selectedCellKeys).map((key) => {
      const [r, c] = key.split(':').map(Number);
      return { row: r, col: c };
    });

    if (isShadeStructureAction(action)) {
      onPatch([buildShadeStructureOperation(action, cellsFromKeys())]);
      clearPlanningSelection();
      return;
    }

    switch (action) {
      case 'create-bed':
        onBedSelection?.(cellsFromKeys());
        clearPlanningSelection();
        break;
      case 'fill-path':
        onPatch([{ op: 'paintCells', cells: cellsFromKeys().map((c) => ({ ...c, kind: 'path' as const })) }]);
        clearPlanningSelection();
        break;
      case 'fill-obstacle':
        onPatch([{ op: 'paintCells', cells: cellsFromKeys().map((c) => ({ ...c, kind: 'obstacle' as const })) }]);
        clearPlanningSelection();
        break;
      case 'greenhouse':
        onPatch([{ op: 'createStructuralObject', cells: cellsFromKeys(), kind: 'covering', subtype: 'greenhouse' }]);
        clearPlanningSelection();
        break;
      case 'delete': {
        const bedCount = planningSelection.beds.length;
        const entityCount = planningSelection.pathCellCount + planningSelection.obstacleCellCount + bedCount;
        const structuralDeleteOps: PlanOperation[] = planningSelection.structuralObjectIds.map(
          (structuralObjectId) => ({ op: 'deleteStructuralObject', structuralObjectId }),
        );
        const paintDeleteOps: PlanOperation[] = entityCount > 0
          ? [{ op: 'paintCells', cells: cellsFromKeys().map((c) => ({ ...c, kind: 'empty' as const })) }]
          : [];
        const deleteOps = [...structuralDeleteOps, ...paintDeleteOps];

        if (bedCount > 0) {
          Modal.confirm({
            title: 'Удалить выделенные объекты?',
            icon: <ExclamationCircleOutlined />,
            content: (
              <div>
                <p>
                  {bedCount > 0 && `Грядок: ${bedCount}. `}
                  {planningSelection.pathCellCount > 0 && `Дорожек: ${planningSelection.pathCellCount}. `}
                  {planningSelection.obstacleCellCount > 0 && `Изгородей: ${planningSelection.obstacleCellCount}. `}
                  {planningSelection.structuralObjectIds.length > 0 && `Объектов: ${planningSelection.structuralObjectIds.length}. `}
                </p>
                <p style={{ marginTop: 8, color: '#666' }}>Если на грядке есть активные посадки, удаление будет отменено.</p>
              </div>
            ),
            okText: 'Удалить',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: async () => {
              await onPatch(deleteOps);
              clearPlanningSelection();
            },
          });
        } else if (deleteOps.length > 0) {
          onPatch(deleteOps);
          clearPlanningSelection();
        }
        break;
      }
      default:
        break;
    }
  }, [planningSelection, onBedSelection, clearPlanningSelection, onPatch]);
};
